import React from 'react';
import { Button } from '@/components/ui/button';
import { VantageLogo, IMAGES } from './MobileScreens';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

// Desktop Screen 8: Landing / Discovery
export const DesktopDiscovery = () => {
  const properties = [
    {
      id: 1,
      location: 'ASPEN, COLORADO',
      name: 'The Summit Chalet',
      price: '$125k',
      yield: '8.5%',
      image: IMAGES.aspen,
      featured: true,
    },
    {
      id: 2,
      location: 'MALIBU, CALIFORNIA',
      name: 'Pacific Edge Villa',
      price: '$185k',
      yield: '7.2%',
      image: IMAGES.malibu,
    },
    {
      id: 3,
      location: 'ST. BARTS, CARIBBEAN',
      name: 'Azure Bay Estate',
      price: '$210k',
      yield: '9.1%',
      image: IMAGES.caribbean,
    },
    {
      id: 4,
      location: 'SWISS ALPS',
      name: 'Alpine Retreat',
      price: '$165k',
      yield: '6.8%',
      image: IMAGES.alps,
    },
  ];

  return (
    <div className="w-full h-full bg-background overflow-hidden flex flex-col">
      {/* Navigation Bar */}
      <nav className="flex items-center justify-between px-8 py-4 border-b border-border/30">
        <VantageLogo size="default" />
        
        <div className="flex items-center gap-8">
          <a href="#" className="text-sm text-foreground hover:text-primary transition-colors">Collection</a>
          <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
          <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">About</a>
        </div>
        
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm">Sign In</Button>
          <Button variant="gold" size="sm">Get Started</Button>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar Filters */}
        <aside className="w-72 border-r border-border/30 p-6 overflow-y-auto">
          <h3 className="font-serif text-lg text-foreground mb-6">Refine Selection</h3>
          
          {/* Property Type */}
          <div className="mb-8">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Property Vibe</h4>
            <div className="space-y-2">
              {['Modern', 'Rustic', 'Beachfront', 'Mountain'].map((vibe) => (
                <label key={vibe} className="flex items-center gap-3 cursor-pointer group">
                  <div className="w-4 h-4 rounded border border-border group-hover:border-primary/50 flex items-center justify-center transition-colors">
                    {vibe === 'Modern' && <div className="w-2 h-2 rounded-sm bg-primary" />}
                  </div>
                  <span className="text-sm text-foreground">{vibe}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Region */}
          <div className="mb-8">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Region</h4>
            <div className="space-y-2">
              {['Americas', 'Europe', 'Caribbean', 'Asia Pacific'].map((region) => (
                <label key={region} className="flex items-center gap-3 cursor-pointer group">
                  <div className="w-4 h-4 rounded border border-border group-hover:border-primary/50 flex items-center justify-center transition-colors">
                    {region === 'Americas' && <div className="w-2 h-2 rounded-sm bg-primary" />}
                  </div>
                  <span className="text-sm text-foreground">{region}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Price Range */}
          <div className="mb-8">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Investment Range</h4>
            <div className="glass rounded-xl p-4">
              <div className="flex justify-between text-sm mb-3">
                <span className="text-foreground">$100k</span>
                <span className="text-foreground">$500k</span>
              </div>
              <div className="h-2 bg-charcoal-lighter rounded-full relative">
                <div className="absolute h-2 rounded-full bg-gradient-to-r from-primary to-gold-bright" style={{ left: '10%', right: '30%' }} />
              </div>
            </div>
          </div>
          
          {/* Yield */}
          <div className="mb-8">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Minimum Yield</h4>
            <div className="grid grid-cols-2 gap-2">
              {['5%+', '7%+', '9%+', '12%+'].map((y) => (
                <button 
                  key={y}
                  className={`py-2 rounded-lg text-sm transition-all ${y === '7%+' ? 'bg-primary/20 border border-primary/50 text-primary' : 'glass text-foreground hover:border-primary/30'}`}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>
          
          <Button variant="gold" className="w-full">Apply Filters</Button>
        </aside>
        
        {/* Property Grid */}
        <main className="flex-1 p-8 overflow-y-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-serif text-3xl text-foreground mb-1">Curated Collection</h1>
              <p className="text-muted-foreground text-sm">12 exceptional properties available</p>
            </div>
            <select className="glass rounded-lg px-4 py-2 text-sm text-foreground bg-transparent border-none outline-none">
              <option>Recently Added</option>
              <option>Highest Yield</option>
              <option>Price: Low to High</option>
            </select>
          </div>
          
          {/* Featured Property */}
          <div className="relative h-80 rounded-2xl overflow-hidden mb-8 property-card cursor-pointer">
            <img src={properties[0].image} alt={properties[0].name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute top-6 left-6">
              <span className="px-3 py-1 rounded-full text-2xs uppercase tracking-wider bg-primary/90 text-primary-foreground font-semibold">Featured</span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <span className="text-2xs uppercase tracking-[0.2em] text-foreground/80 block mb-2">{properties[0].location}</span>
              <h2 className="font-serif text-3xl text-foreground mb-4">{properties[0].name}</h2>
              <div className="flex items-center gap-6">
                <span className="text-primary text-xl font-semibold">{properties[0].price}<span className="text-foreground/60 text-sm font-normal"> / share</span></span>
                <span className="text-foreground/80 text-sm">{properties[0].yield} Annual Yield</span>
              </div>
            </div>
          </div>
          
          {/* Property Grid */}
          <div className="grid grid-cols-3 gap-6">
            {properties.slice(1).map((property) => (
              <div key={property.id} className="relative rounded-xl overflow-hidden property-card cursor-pointer group">
                <div className="aspect-[4/3]">
                  <img src={property.image} alt={property.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="text-2xs uppercase tracking-[0.15em] text-foreground/70 block mb-1">{property.location}</span>
                  <h3 className="font-serif text-lg text-foreground mb-2">{property.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-primary font-medium">{property.price}<span className="text-foreground/50 text-xs font-normal"> / share</span></span>
                    <span className="text-foreground/70 text-xs">{property.yield} yield</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

// Desktop Screen 9: Property Details
export const DesktopPropertyDetails = () => {
  const images = [IMAGES.aspen, IMAGES.alps, IMAGES.modern];
  const [activeImage, setActiveImage] = React.useState(0);
  
  return (
    <div className="w-full h-full bg-background overflow-hidden flex flex-col">
      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-4 border-b border-border/30">
        <div className="flex items-center gap-6">
          <button className="flex items-center gap-2 text-foreground hover:text-primary transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Collection
          </button>
        </div>
        <VantageLogo size="default" />
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm">Save</Button>
          <Button variant="goldOutline" size="sm">Share</Button>
        </div>
      </nav>
      
      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Image Gallery */}
        <div className="w-3/5 p-8 flex flex-col">
          <div className="flex-1 relative rounded-2xl overflow-hidden mb-4">
            <img src={images[activeImage]} alt="Property" className="w-full h-full object-cover" />
            <div className="absolute bottom-4 right-4 flex gap-2">
              <button className="w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-white/20 transition-colors">
                <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button className="w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-white/20 transition-colors">
                <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          <div className="flex gap-3">
            {images.map((img, i) => (
              <button 
                key={i}
                onClick={() => setActiveImage(i)}
                className={`w-24 h-16 rounded-lg overflow-hidden transition-all ${activeImage === i ? 'ring-2 ring-primary' : 'opacity-60 hover:opacity-100'}`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
        
        {/* Details Panel */}
        <aside className="w-2/5 glass-heavy border-l border-border/30 p-8 overflow-y-auto">
          <span className="text-2xs uppercase tracking-[0.2em] text-muted-foreground">ASPEN, COLORADO</span>
          <h1 className="font-serif text-3xl text-foreground mt-2 mb-6">The Summit Chalet</h1>
          
          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="glass rounded-xl p-4">
              <span className="text-xs text-muted-foreground block mb-1">Annual Yield</span>
              <span className="text-2xl font-semibold text-primary">8.5%</span>
            </div>
            <div className="glass rounded-xl p-4">
              <span className="text-xs text-muted-foreground block mb-1">Appreciation</span>
              <span className="text-2xl font-semibold text-primary">12%</span>
            </div>
            <div className="glass rounded-xl p-4">
              <span className="text-xs text-muted-foreground block mb-1">Share Price</span>
              <span className="text-2xl font-semibold text-foreground">$125k</span>
            </div>
            <div className="glass rounded-xl p-4">
              <span className="text-xs text-muted-foreground block mb-1">Total Value</span>
              <span className="text-2xl font-semibold text-foreground">$1M</span>
            </div>
          </div>
          
          {/* Amenities */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-foreground mb-4">Property Amenities</h3>
            <div className="flex flex-wrap gap-2">
              {['Hot Tub', 'Ski-In Access', 'Concierge', 'Cinema', 'Wine Cellar', 'Helipad'].map((amenity) => (
                <span key={amenity} className="px-3 py-1.5 rounded-full glass text-xs text-foreground">
                  {amenity}
                </span>
              ))}
            </div>
          </div>
          
          {/* Description */}
          <div className="mb-8">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Perched at 9,000 feet with panoramic views of the Elk Mountains, The Summit Chalet represents the pinnacle of alpine luxury. This architectural masterpiece seamlessly blends contemporary design with mountain warmth.
            </p>
          </div>
          
          <Button variant="gold" size="lg" className="w-full mb-4">
            Invest Now
          </Button>
          <Button variant="outline" size="lg" className="w-full">
            Schedule Viewing
          </Button>
        </aside>
      </div>
    </div>
  );
};

// Desktop Screen 10: Investment Modeling
export const DesktopInvestmentModeling = () => {
  // Fixed share increments: 1/8 = $125k, 2/8 = $250k, 3/8 = $375k, 4/8 = $500k
  const shareOptions = [125000, 250000, 375000, 500000];
  const [shareIndex, setShareIndex] = React.useState(2); // Default to 3/8 share ($375k)
  const investment = shareOptions[shareIndex];
  const shareLabels = ['1/8 Share', '2/8 Share', '3/8 Share', '4/8 Share'];
  const annualYield = 0.085;
  const appreciation = 0.12;
  
  const rentalIncome = Math.round(investment * annualYield);
  const year5Value = Math.round(investment * Math.pow(1 + appreciation, 5));
  const totalReturn = Math.round((year5Value - investment) + (rentalIncome * 5));
  
  return (
    <div className="w-full h-full bg-background overflow-hidden flex flex-col">
      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-4 border-b border-border/30">
        <div className="flex items-center gap-6">
          <button className="flex items-center gap-2 text-foreground hover:text-primary transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Property
          </button>
        </div>
        <VantageLogo size="default" />
        <Button variant="goldOutline" size="sm">Download Projections</Button>
      </nav>
      
      {/* Content */}
      <div className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-5xl mx-auto">
          <h1 className="font-serif text-3xl text-foreground mb-2">Investment Modeling</h1>
          <p className="text-muted-foreground mb-8">The Summit Chalet • Aspen, Colorado</p>
          
          {/* Main Slider */}
          <div className="glass-heavy rounded-2xl p-8 mb-8">
            <h3 className="text-lg font-semibold text-foreground mb-6">Select Investment Amount</h3>
            <div className="flex justify-between mb-4">
              <div>
                <span className="text-xs text-muted-foreground block">1/8 Share</span>
                <span className="text-foreground font-medium">$125,000</span>
              </div>
              <div className="text-center">
                <span className="text-xs text-muted-foreground block">{shareLabels[shareIndex]}</span>
                <span className="text-3xl font-serif text-primary">${investment.toLocaleString()}</span>
              </div>
              <div className="text-right">
                <span className="text-xs text-muted-foreground block">4/8 Share</span>
                <span className="text-foreground font-medium">$500,000</span>
              </div>
            </div>
            
            {/* Gold Slider - Fixed increments */}
            <div className="py-6">
              <input
                type="range"
                min="0"
                max="3"
                step="1"
                value={shareIndex}
                onChange={(e) => setShareIndex(Number(e.target.value))}
                className="w-full h-3 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, hsl(45 65% 52%) ${(shareIndex / 3) * 100}%, hsl(0 0% 18%) ${(shareIndex / 3) * 100}%)`,
                }}
              />
            </div>
            
            {/* Share markers */}
            <div className="flex justify-between px-1 -mt-2">
              {shareLabels.map((label, i) => (
                <button 
                  key={i}
                  onClick={() => setShareIndex(i)}
                  className={`text-xs transition-colors ${shareIndex === i ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Projections Grid */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-muted-foreground">Annual Rental Income</span>
              </div>
              <span className="text-3xl font-serif text-foreground">${rentalIncome.toLocaleString()}</span>
              <span className="text-xs text-primary ml-2">8.5% yield</span>
            </div>
            
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <span className="text-sm text-muted-foreground">5-Year Property Value</span>
              </div>
              <span className="text-3xl font-serif text-foreground">${year5Value.toLocaleString()}</span>
              <span className="text-xs text-primary ml-2">+12% p.a.</span>
            </div>
            
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="text-sm text-muted-foreground">Total 5-Year Return</span>
              </div>
              <span className="text-3xl font-serif text-primary">${totalReturn.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground ml-2">projected</span>
            </div>
          </div>
          
          {/* Static Chart */}
          <div className="glass-heavy rounded-2xl p-8">
            <h3 className="text-lg font-semibold text-foreground mb-6">Projected Growth</h3>
            <div className="h-48 relative">
              <svg viewBox="0 0 600 180" className="w-full h-full">
                <defs>
                  <linearGradient id="desktopChartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="hsl(45 65% 52%)" stopOpacity="0.3"/>
                    <stop offset="100%" stopColor="hsl(45 65% 52%)" stopOpacity="0"/>
                  </linearGradient>
                </defs>
                {/* Grid lines */}
                {[0, 1, 2, 3, 4].map((i) => (
                  <line key={i} x1="40" y1={35 * i + 20} x2="580" y2={35 * i + 20} stroke="hsl(0 0% 20%)" strokeWidth="1" />
                ))}
                {/* Chart line */}
                <path 
                  d="M60,150 Q150,140 200,120 T340,80 T480,40 T560,20" 
                  fill="none" 
                  stroke="hsl(45 65% 52%)" 
                  strokeWidth="3"
                />
                <path 
                  d="M60,150 Q150,140 200,120 T340,80 T480,40 T560,20 V170 H60 Z" 
                  fill="url(#desktopChartGradient)"
                />
                {/* Labels */}
                <text x="60" y="175" fill="hsl(0 0% 60%)" fontSize="10">Year 1</text>
                <text x="180" y="175" fill="hsl(0 0% 60%)" fontSize="10">Year 2</text>
                <text x="300" y="175" fill="hsl(0 0% 60%)" fontSize="10">Year 3</text>
                <text x="420" y="175" fill="hsl(0 0% 60%)" fontSize="10">Year 4</text>
                <text x="540" y="175" fill="hsl(0 0% 60%)" fontSize="10">Year 5</text>
              </svg>
            </div>
          </div>
          
          <div className="flex gap-4 mt-8">
            <Button variant="gold" size="lg" className="flex-1">
              Proceed to Investment
            </Button>
            <Button variant="outline" size="lg">
              Speak with Advisor
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Desktop Screen 11: Portfolio Command Center
export const DesktopPortfolioCommand = () => {
  const portfolioValue = 685000;
  const totalReturn = 15.8;
  
  const holdings = [
    { name: 'The Summit Chalet', location: 'Aspen, CO', value: 155000, return: 12.4, share: '1/8', image: IMAGES.aspen },
    { name: 'Pacific Edge Villa', location: 'Malibu, CA', value: 210000, return: 18.2, share: '1/6', image: IMAGES.malibu },
    { name: 'Azure Bay Estate', location: 'St. Barts', value: 185000, return: 14.7, share: '1/8', image: IMAGES.caribbean },
    { name: 'Alpine Retreat', location: 'Swiss Alps', value: 135000, return: 11.3, share: '1/10', image: IMAGES.alps },
  ];
  
  return (
    <div className="w-full h-full bg-background overflow-hidden flex flex-col">
      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-4 border-b border-border/30">
        <VantageLogo size="default" />
        <div className="flex items-center gap-8">
          <a href="#" className="text-sm text-foreground border-b-2 border-primary pb-1">Portfolio</a>
          <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Collection</a>
          <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Concierge</a>
        </div>
        <div className="flex items-center gap-4">
          <button className="w-10 h-10 rounded-full glass flex items-center justify-center">
            <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </button>
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-sm font-medium text-primary">JD</span>
          </div>
        </div>
      </nav>
      
      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main Dashboard */}
        <main className="flex-1 p-8 overflow-y-auto">
          {/* Top Stats */}
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="glass-heavy rounded-2xl p-6">
              <span className="text-xs text-muted-foreground block mb-2">Total Portfolio Value</span>
              <span className="text-3xl font-serif text-foreground">${portfolioValue.toLocaleString()}</span>
              <span className="text-sm text-primary ml-2">+{totalReturn}%</span>
            </div>
            <div className="glass rounded-xl p-6">
              <span className="text-xs text-muted-foreground block mb-2">Properties Owned</span>
              <span className="text-3xl font-serif text-foreground">4</span>
            </div>
            <div className="glass rounded-xl p-6">
              <span className="text-xs text-muted-foreground block mb-2">Annual Income</span>
              <span className="text-3xl font-serif text-foreground">$58,225</span>
            </div>
            <div className="glass rounded-xl p-6">
              <span className="text-xs text-muted-foreground block mb-2">Available Days</span>
              <span className="text-3xl font-serif text-foreground">42</span>
            </div>
          </div>
          
          {/* Map & Chart Row */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            {/* Global Map */}
            <div className="glass-heavy rounded-2xl p-6 h-72">
              <h3 className="text-sm font-semibold text-foreground mb-4">Global Holdings</h3>
              <div className="relative h-full flex items-center justify-center">
                {/* Simplified World Map SVG */}
                <svg viewBox="0 0 400 200" className="w-full h-48 opacity-30">
                  <path d="M30,100 Q100,60 150,80 T250,70 T350,90" fill="none" stroke="hsl(0 0% 40%)" strokeWidth="1"/>
                  <path d="M50,120 Q120,100 180,110 T280,100 T370,110" fill="none" stroke="hsl(0 0% 40%)" strokeWidth="1"/>
                </svg>
                {/* Gold pins */}
                <div className="absolute" style={{ top: '35%', left: '20%' }}>
                  <div className="w-3 h-3 rounded-full bg-primary shadow-gold-strong animate-pulse" />
                </div>
                <div className="absolute" style={{ top: '40%', left: '15%' }}>
                  <div className="w-3 h-3 rounded-full bg-primary shadow-gold-strong animate-pulse" />
                </div>
                <div className="absolute" style={{ top: '50%', left: '35%' }}>
                  <div className="w-3 h-3 rounded-full bg-primary shadow-gold-strong animate-pulse" />
                </div>
                <div className="absolute" style={{ top: '30%', left: '55%' }}>
                  <div className="w-3 h-3 rounded-full bg-primary shadow-gold-strong animate-pulse" />
                </div>
              </div>
            </div>
            
            {/* Performance Chart */}
            <div className="glass-heavy rounded-2xl p-6 h-72">
              <h3 className="text-sm font-semibold text-foreground mb-4">Portfolio Performance</h3>
              <div className="h-48 relative">
                <svg viewBox="0 0 300 150" className="w-full h-full">
                  <defs>
                    <linearGradient id="portfolioGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="hsl(45 65% 52%)" stopOpacity="0.4"/>
                      <stop offset="100%" stopColor="hsl(45 65% 52%)" stopOpacity="0"/>
                    </linearGradient>
                  </defs>
                  <path 
                    d="M10,120 Q40,115 70,100 T130,85 T190,60 T250,45 T290,30" 
                    fill="none" 
                    stroke="hsl(45 65% 52%)" 
                    strokeWidth="2"
                  />
                  <path 
                    d="M10,120 Q40,115 70,100 T130,85 T190,60 T250,45 T290,30 V140 H10 Z" 
                    fill="url(#portfolioGradient)"
                  />
                </svg>
              </div>
            </div>
          </div>
          
          {/* Holdings Table */}
          <div className="glass-heavy rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-border/30">
              <h3 className="text-lg font-semibold text-foreground">Holdings</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border/30">
                  <th className="text-left text-xs uppercase tracking-wider text-muted-foreground p-4">Property</th>
                  <th className="text-left text-xs uppercase tracking-wider text-muted-foreground p-4">Share</th>
                  <th className="text-right text-xs uppercase tracking-wider text-muted-foreground p-4">Value</th>
                  <th className="text-right text-xs uppercase tracking-wider text-muted-foreground p-4">Return</th>
                  <th className="text-right text-xs uppercase tracking-wider text-muted-foreground p-4"></th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((holding) => (
                  <tr key={holding.name} className="border-b border-border/20 hover:bg-charcoal-light/50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-4">
                        <img src={holding.image} alt={holding.name} className="w-12 h-12 rounded-lg object-cover" />
                        <div>
                          <span className="font-serif text-foreground block">{holding.name}</span>
                          <span className="text-xs text-muted-foreground">{holding.location}</span>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-foreground">{holding.share}</td>
                    <td className="p-4 text-right text-foreground font-medium">${holding.value.toLocaleString()}</td>
                    <td className="p-4 text-right text-primary">+{holding.return}%</td>
                    <td className="p-4 text-right">
                      <Button variant="ghost" size="sm">View</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    </div>
  );
};

// Desktop Screen 12: Concierge & Ownership
export const DesktopConcierge = () => {
  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const weeks = [
    [1, 2, 3, 4, 5, 6, 7],
    [8, 9, 10, 11, 12, 13, 14],
    [15, 16, 17, 18, 19, 20, 21],
    [22, 23, 24, 25, 26, 27, 28],
    [29, 30, 31, null, null, null, null],
  ];
  
  const services = [
    { icon: '🍽', name: 'Private Chef', desc: 'Gourmet dining at your residence' },
    { icon: '🛒', name: 'Stock Fridge', desc: 'Pre-arrival grocery shopping' },
    { icon: '✈', name: 'Airport Transfer', desc: 'Luxury ground transportation' },
    { icon: '🎿', name: 'Ski Concierge', desc: 'Equipment & lift passes' },
    { icon: '💆', name: 'Spa Services', desc: 'In-residence wellness' },
    { icon: '🍷', name: 'Wine Selection', desc: 'Curated cellar stocking' },
  ];
  
  return (
    <div className="w-full h-full bg-background overflow-hidden flex flex-col">
      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-4 border-b border-border/30">
        <VantageLogo size="default" />
        <div className="flex items-center gap-8">
          <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Portfolio</a>
          <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Collection</a>
          <a href="#" className="text-sm text-foreground border-b-2 border-primary pb-1">Concierge</a>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-sm font-medium text-primary">JD</span>
          </div>
        </div>
      </nav>
      
      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Property Selector */}
        <aside className="w-80 border-r border-border/30 p-6 overflow-y-auto">
          <h3 className="font-serif text-lg text-foreground mb-4">Your Properties</h3>
          <div className="space-y-3">
            {[
              { name: 'The Summit Chalet', location: 'Aspen, CO', image: IMAGES.aspen, active: true },
              { name: 'Pacific Edge Villa', location: 'Malibu, CA', image: IMAGES.malibu },
              { name: 'Azure Bay Estate', location: 'St. Barts', image: IMAGES.caribbean },
            ].map((prop) => (
              <button 
                key={prop.name}
                className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all ${prop.active ? 'glass-heavy gold-border' : 'hover:bg-charcoal-light'}`}
              >
                <img src={prop.image} alt={prop.name} className="w-14 h-14 rounded-lg object-cover" />
                <div className="text-left">
                  <span className="font-serif text-foreground block">{prop.name}</span>
                  <span className="text-xs text-muted-foreground">{prop.location}</span>
                </div>
              </button>
            ))}
          </div>
          
          {/* Stay History */}
          <div className="mt-8">
            <h3 className="font-serif text-lg text-foreground mb-4">Stay History</h3>
            <div className="space-y-3">
              <div className="glass rounded-lg p-3">
                <span className="text-sm text-foreground block">Dec 18-22, 2024</span>
                <span className="text-xs text-muted-foreground">The Summit Chalet</span>
              </div>
              <div className="glass rounded-lg p-3">
                <span className="text-sm text-foreground block">Nov 5-10, 2024</span>
                <span className="text-xs text-muted-foreground">Pacific Edge Villa</span>
              </div>
            </div>
          </div>
        </aside>
        
        {/* Main Content */}
        <main className="flex-1 p-8 overflow-y-auto">
          <div className="grid grid-cols-2 gap-8">
            {/* Calendar */}
            <div className="glass-heavy rounded-2xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-serif text-xl text-foreground">December 2024</h3>
                <div className="flex gap-2">
                  <button className="w-8 h-8 rounded-full glass flex items-center justify-center">
                    <svg className="w-4 h-4 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button className="w-8 h-8 rounded-full glass flex items-center justify-center">
                    <svg className="w-4 h-4 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Days Header */}
              <div className="grid grid-cols-7 gap-2 mb-4">
                {days.map((day, i) => (
                  <span key={i} className="text-center text-xs text-muted-foreground">{day}</span>
                ))}
              </div>
              
              {/* Calendar Grid */}
              <div className="space-y-2">
                {weeks.map((week, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-2">
                    {week.map((date, di) => (
                      <button 
                        key={di}
                        disabled={!date}
                        className={`h-10 rounded-lg flex items-center justify-center text-sm transition-all ${
                          !date ? 'invisible' :
                          date >= 18 && date <= 22 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-charcoal-lighter text-foreground'
                        }`}
                      >
                        {date}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
              
              <Button variant="gold" size="lg" className="w-full mt-6">
                Schedule Your Stay
              </Button>
            </div>
            
            {/* Concierge Services */}
            <div>
              <h3 className="font-serif text-xl text-foreground mb-6">Concierge Services</h3>
              <div className="grid grid-cols-2 gap-4">
                {services.map((service) => (
                  <button key={service.name} className="glass rounded-xl p-4 text-left hover:gold-border transition-all group">
                    <span className="text-2xl block mb-2">{service.icon}</span>
                    <span className="font-serif text-foreground block group-hover:text-primary transition-colors">{service.name}</span>
                    <span className="text-xs text-muted-foreground">{service.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// Desktop Screen 13: Advanced Filters
export const DesktopAdvancedFilters = () => {
  const vibes = ['Modern', 'Rustic', 'Beachfront', 'Mountain', 'Urban', 'Historic'];
  const regions = ['Americas', 'Europe', 'Caribbean', 'Asia Pacific', 'Middle East'];
  
  return (
    <div className="w-full h-full bg-background overflow-hidden flex flex-col">
      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-4 border-b border-border/30">
        <VantageLogo size="default" />
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm">Clear All</Button>
          <Button variant="goldOutline" size="sm">Save Search</Button>
        </div>
      </nav>
      
      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Rail Filters */}
        <aside className="w-80 glass-heavy border-r border-border/30 p-6 overflow-y-auto">
          <h2 className="font-serif text-xl text-foreground mb-6">Advanced Filters</h2>
          
          {/* Property Vibe */}
          <div className="mb-8">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-4">Property Vibe</h4>
            <div className="flex flex-wrap gap-2">
              {vibes.map((vibe, i) => (
                <button
                  key={vibe}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${
                    i < 2 ? 'bg-primary/20 border border-primary/50 text-primary' : 'glass text-foreground hover:border-primary/30'
                  }`}
                >
                  {vibe}
                </button>
              ))}
            </div>
          </div>
          
          {/* Region */}
          <div className="mb-8">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-4">Region</h4>
            <div className="space-y-2">
              {regions.map((region, i) => (
                <label key={region} className="flex items-center gap-3 cursor-pointer group">
                  <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${i === 0 ? 'border-primary bg-primary/20' : 'border-border group-hover:border-primary/50'}`}>
                    {i === 0 && <svg className="w-3 h-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                  </div>
                  <span className="text-sm text-foreground">{region}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Investment Range */}
          <div className="mb-8">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-4">Investment Range</h4>
            <div className="glass rounded-xl p-4">
              <div className="flex justify-between text-sm mb-3">
                <span className="text-primary font-medium">$100k</span>
                <span className="text-primary font-medium">$400k</span>
              </div>
              <div className="h-2 bg-charcoal-lighter rounded-full relative">
                <div className="absolute h-2 rounded-full bg-gradient-to-r from-primary to-gold-bright" style={{ left: '15%', right: '25%' }} />
                <div className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-primary border-2 border-gold-bright shadow-gold-strong" style={{ left: 'calc(15% - 10px)' }} />
                <div className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-primary border-2 border-gold-bright shadow-gold-strong" style={{ left: 'calc(75% - 10px)' }} />
              </div>
            </div>
          </div>
          
          {/* Yield */}
          <div className="mb-8">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-4">Minimum Yield</h4>
            <div className="grid grid-cols-2 gap-2">
              {['5%+', '7%+', '9%+', '12%+'].map((y, i) => (
                <button 
                  key={y}
                  className={`py-2.5 rounded-lg text-sm transition-all ${i === 1 ? 'bg-primary/20 border border-primary/50 text-primary' : 'glass text-foreground hover:border-primary/30'}`}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>
          
          {/* Bedrooms */}
          <div className="mb-8">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-4">Bedrooms</h4>
            <div className="flex gap-2">
              {['2+', '3+', '4+', '5+', '6+'].map((beds, i) => (
                <button 
                  key={beds}
                  className={`flex-1 py-2 rounded-lg text-sm transition-all ${i === 2 ? 'bg-primary/20 border border-primary/50 text-primary' : 'glass text-foreground hover:border-primary/30'}`}
                >
                  {beds}
                </button>
              ))}
            </div>
          </div>
          
          <Button variant="gold" className="w-full">
            Show 8 Properties
          </Button>
        </aside>
        
        {/* Results Preview */}
        <main className="flex-1 p-8 overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl text-foreground">8 Properties Match</h2>
            <span className="text-sm text-muted-foreground">Updating in real-time</span>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            {[
              { img: IMAGES.aspen, name: 'The Summit Chalet', location: 'ASPEN, COLORADO', price: '$125k', yield: '8.5%' },
              { img: IMAGES.malibu, name: 'Pacific Edge Villa', location: 'MALIBU, CALIFORNIA', price: '$185k', yield: '7.2%' },
              { img: IMAGES.caribbean, name: 'Azure Bay Estate', location: 'ST. BARTS, CARIBBEAN', price: '$210k', yield: '9.1%' },
              { img: IMAGES.alps, name: 'Alpine Retreat', location: 'SWISS ALPS', price: '$165k', yield: '6.8%' },
            ].map((property, i) => (
              <div key={i} className="relative rounded-xl overflow-hidden property-card cursor-pointer group opacity-90">
                <div className="aspect-[4/3]">
                  <img src={property.img} alt={property.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="text-2xs uppercase tracking-[0.15em] text-foreground/70 block mb-1">{property.location}</span>
                  <h3 className="font-serif text-lg text-foreground mb-2">{property.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-primary font-medium">{property.price}<span className="text-foreground/50 text-xs font-normal"> / share</span></span>
                    <span className="text-foreground/70 text-xs">{property.yield} yield</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

// Desktop Screen 14: Trust & Structure
export const DesktopTrustStructure = () => {
  return (
    <div className="w-full h-full bg-background overflow-hidden flex flex-col">
      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-4 border-b border-border/30">
        <VantageLogo size="default" />
        <div className="flex items-center gap-8">
          <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Collection</a>
          <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
          <a href="#" className="text-sm text-foreground border-b-2 border-primary pb-1">Trust & Security</a>
        </div>
        <Button variant="gold" size="sm">Get Started</Button>
      </nav>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Hero */}
        <div className="px-8 py-16 text-center max-w-4xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] text-primary mb-4 block">INSTITUTIONAL-GRADE SECURITY</span>
          <h1 className="font-serif text-5xl text-foreground mb-6">Built on Trust</h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            VANTAGE operates under a rigorous regulatory framework, ensuring your investments are protected by the same standards used by family offices and institutional investors worldwide.
          </p>
        </div>
        
        {/* Structure Section */}
        <div className="px-8 pb-16">
          <div className="max-w-6xl mx-auto">
            {/* Ownership Structure */}
            <div className="glass-heavy rounded-2xl p-8 mb-8">
              <h2 className="font-serif text-2xl text-foreground mb-6">Fractional Ownership Structure</h2>
              <div className="grid grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full glass mx-auto mb-4 flex items-center justify-center">
                    <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h3 className="font-serif text-lg text-foreground mb-2">Property LLC</h3>
                  <p className="text-sm text-muted-foreground">Each property held in a dedicated single-asset LLC structure</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full glass mx-auto mb-4 flex items-center justify-center">
                    <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="font-serif text-lg text-foreground mb-2">SEC Compliance</h3>
                  <p className="text-sm text-muted-foreground">Fully compliant with securities regulations</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full glass mx-auto mb-4 flex items-center justify-center">
                    <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="font-serif text-lg text-foreground mb-2">Secure Custody</h3>
                  <p className="text-sm text-muted-foreground">Shares held with licensed custodians</p>
                </div>
              </div>
            </div>
            
            {/* Trust Badges */}
            <div className="grid grid-cols-4 gap-6 mb-8">
              {[
                { label: 'Assets Under Management', value: '$2.4B' },
                { label: 'Properties in Portfolio', value: '47' },
                { label: 'Investor Members', value: '8,200+' },
                { label: 'Years Operating', value: '7' },
              ].map((stat) => (
                <div key={stat.label} className="glass rounded-xl p-6 text-center">
                  <span className="text-3xl font-serif text-primary block mb-2">{stat.value}</span>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</span>
                </div>
              ))}
            </div>
            
            {/* Legal & Documents */}
            <div className="glass-heavy rounded-2xl p-8">
              <h2 className="font-serif text-2xl text-foreground mb-6">Legal Documentation</h2>
              <div className="grid grid-cols-2 gap-4">
                {[
                  'Operating Agreement',
                  'Private Placement Memorandum',
                  'Subscription Agreement',
                  'Property Inspection Reports',
                  'Title Insurance Documentation',
                  'Financial Audits (Annual)',
                ].map((doc) => (
                  <button key={doc} className="flex items-center gap-4 p-4 rounded-xl glass hover:gold-border transition-all group">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <span className="text-foreground group-hover:text-primary transition-colors">{doc}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Components are exported inline as named exports
